import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import {
  cancelCFDIInvoiceV2,
  createCFDI,
  createGlobalByPaymentMethodCFDI,
  downloadCFDIPDFV2,
  downloadCFDIZIP,
  GlobalPaymentMethodPayload,
  previewGlobalByPaymentMethodCFDI,
  searchGlobalByPaymentMethodCandidates,
  sendCFDIByEmailV2
} from '../../services/invoice.service';
import { listQueryKey as ordersListQueryKey } from '../orders/queries';
import { trackEvent } from '../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../utils/monitoring/Monitoring_Events_Enum';
import { usePostHog } from 'posthog-js/react';
import type { CreateCFDIRequest } from 'washday-sdk';

const listQueryKey = 'cfdi';

export const useDeleteCFDI = () => {
  const queryClient = useQueryClient()
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async (data: {
      id: string;
      motive: string
      substitution?: string
    }) => {
      try {
        const res = await cancelCFDIInvoiceV2(data.id, {
          motive: data.motive,
          substitution: data.substitution
        });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        trackEvent(posthog, MonitoringEventTypes.CFDI_INVOICE_CANCEL_SUCCESS, {
          operation: 'cancelCFDIInvoiceV2',
          invoiceId: data.id,
          motive: data.motive,
          hasSubstitution: !!data.substitution
        });
        return res.data.data;
      } catch (e: any) {
        trackEvent(posthog, MonitoringEventTypes.CFDI_INVOICE_CANCEL_FAIL, {
          operation: 'cancelCFDIInvoiceV2',
          invoiceId: data.id,
          motive: data.motive,
          hasSubstitution: !!data.substitution,
          error: e
        });
        backendErrorsHandler(e);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      })]);
    },
  });
};

export const useCreateCFDI = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async (data: CreateCFDIRequest) => {
      const isGlobal = !!data.CFDIInfo.global;
      const cfdiScope = isGlobal ? 'global' : 'order';
      const cfdiType = isGlobal ? 'global_cfdi' : 'orders_cfdi';
      try {
        const res = await createCFDI(data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        const cfdiId = res.data.data.id ?? res.data.data._id;
        trackEvent(posthog, MonitoringEventTypes.CFDI_INVOICE_CREATE_SUCCESS, {
          operation: 'createCFDI',
          cfdiId,
          cfdiScope,
          cfdiType,
          orderCount: data.ordersID.length,
          hasCustomer: !!data.customer,
          paymentMethod: data.CFDIInfo.payment_method,
          paymentForm: data.CFDIInfo.payment_form,
          isGlobal
        });
        return res.data.data;
      } catch (e: any) {
        trackEvent(posthog, MonitoringEventTypes.CFDI_INVOICE_CREATE_FAIL, {
          operation: 'createCFDI',
          cfdiScope,
          cfdiType,
          orderCount: data.ordersID.length,
          hasCustomer: !!data.customer,
          paymentMethod: data.CFDIInfo.payment_method,
          paymentForm: data.CFDIInfo.payment_form,
          isGlobal,
          error: e
        });
        backendErrorsHandler(e, { showFallback: true });
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: [listQueryKey],
      }), queryClient.invalidateQueries({
        queryKey: [ordersListQueryKey],
      })]);
    }
  });
};

export const useDownloadCFDIPDF = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await downloadCFDIPDFV2(id);
        if (!res) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${id}_${new Date().toISOString()}.pdf`);
        document.body.appendChild(link);
        link.click();
        return res;
      } catch (e: any) {
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useDownloadCFDIZIP = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await downloadCFDIZIP(id);
        if (!res) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${id}_${new Date().toISOString()}.zip`);
        document.body.appendChild(link);
        link.click();
        return res;
      } catch (e: any) {
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useSendCFDIEmail = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await sendCFDIByEmailV2(id);
        if (!res) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res;
      } catch (e: any) {
        backendErrorsHandler(e);
        throw e;
      }
    }
  });
};

export const useSearchGlobalPaymentMethodCandidates = () => {
  return useMutation({
    mutationFn: async (data: GlobalPaymentMethodPayload) => {
      const res = await searchGlobalByPaymentMethodCandidates(data);
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data;
    }
  });
};

export const usePreviewGlobalPaymentMethodCFDI = () => {
  return useMutation({
    mutationFn: async (data: GlobalPaymentMethodPayload) => {
      const res = await previewGlobalByPaymentMethodCFDI(data);
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while previewing.');
      }
      return res.data.data;
    }
  });
};

export const useCreateGlobalPaymentMethodCFDI = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async (data: GlobalPaymentMethodPayload) => {
      const paymentLineCount = 'paymentLines' in data ? data.paymentLines.length : 0;
      const includeAllStores = 'includeAllStores' in data ? !!data.includeAllStores : false;
      const cfdiScope = 'global';
      const cfdiType = 'global_payment_method_cfdi';
      try {
        const res = await createGlobalByPaymentMethodCFDI(data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        const cfdiId = res.data.data.facturapiInvoiceID
          ?? res.data.data.invoice?.id
          ?? res.data.data.id
          ?? res.data.data._id;
        trackEvent(posthog, MonitoringEventTypes.CFDI_INVOICE_CREATE_SUCCESS, {
          operation: 'createGlobalByPaymentMethodCFDI',
          cfdiId,
          cfdiScope,
          cfdiType,
          paymentMethod: data.paymentMethod,
          paymentLineCount,
          includeAllStores
        });
        return res.data.data;
      } catch (e: any) {
        trackEvent(posthog, MonitoringEventTypes.CFDI_INVOICE_CREATE_FAIL, {
          operation: 'createGlobalByPaymentMethodCFDI',
          cfdiScope,
          cfdiType,
          paymentMethod: data.paymentMethod,
          paymentLineCount,
          includeAllStores,
          error: e
        });
        backendErrorsHandler(e, { showFallback: true });
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [listQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [ordersListQueryKey] })
      ]);
    }
  });
};
